import React from "react";
import { MathJax } from "better-react-mathjax";
import "./ROGFrameworkPage.css";

const F = ({ children }) => (
    <div className="rog-formula-block">
        <MathJax dynamic>
            <span>{children}</span>
        </MathJax>
    </div>
);

const ROGFrameworkPage = () => {
    return (
        <div className="rog-page">
            <div className="rog-container">

                {/* Header */}
                <div className="rog-header">
                    <h1>The ROG Framework</h1>
                    <p>
                        A structural framework for solving mathematical problems by transforming
                        unfamiliar expressions into recognizable forms using three fundamental methods:
                        <strong> Rewriting</strong>, <strong>Operation</strong>, and <strong>Generation</strong>.
                    </p>
                </div>

                {/* Known set */}
                <div className="rog-known-set">
                    <h3>Known Recognizable Terms</h3>
                    <p>
                        Let <MathJax inline dynamic>{"$T$"}</MathJax> be any mathematical object
                        (function, integral, sum, expression, …). We assume the existence of a set
                        of <em>known recognizable terms</em>:
                    </p>
                    <F>{"$cc K = {T_(k1), T_(k2), ..., T_(kn)}$"}</F>
                    <p>
                        for which properties, primitives, or closed forms are already known.
                        The goal of each method is to express <MathJax inline dynamic>{"$T$"}</MathJax> in
                        terms of elements of <MathJax inline dynamic>{"$cc K$"}</MathJax>.
                    </p>
                </div>

                {/* Three method cards */}
                <div className="rog-methods">

                    {/* Rewriting */}
                    <div className="rog-method-card">
                        <div className="rog-method-header rewriting">
                            <div className="rog-letter">R</div>
                            <div>
                                <h2>Rewriting — Structure-Preserving</h2>
                                <p>Transform the expression without changing its value.</p>
                            </div>
                        </div>
                        <div className="rog-method-body">
                            <div className="rog-definition">
                                <h4>Formal Definition</h4>
                                <p>
                                    Define the rewriting operator <MathJax inline dynamic>{"$\"Rew\"$"}</MathJax> such that:
                                </p>
                                <F>{"$\"Rew\"(T) = f(T_(k1), T_(k2), ..., T_(kn))$"}</F>
                                <div className="rog-condition">
                                    <span className="rog-tag">{"Rew(T) = T  (value unchanged)"}</span>
                                    <span className="rog-tag">{"f is a composition of algebraic operations"}</span>
                                </div>
                            </div>
                            <div className="rog-example">
                                <h4>Example — Integral Decomposition</h4>
                                <p>Compute:</p>
                                <F>{"$int x/(x+1) dx$"}</F>
                                <p>Rewrite the integrand by adding and subtracting 1 in the numerator:</p>
                                <F>{"$x/(x+1) = (x+1-1)/(x+1) = 1 - 1/(x+1)$"}</F>
                                <p>The integral splits into two recognizable terms:</p>
                                <F>{"$int x/(x+1) dx = int 1 dx - int 1/(x+1) dx = x - ln|x+1| + C$"}</F>
                            </div>

                            <div className="rog-example">
                                <h4>Example — Telescoping Sum</h4>
                                <p>Compute:</p>
                                <F>{"$sum_(n=1)^N 1/(n(n+1))$"}</F>
                                <p>
                                    Rewrite via partial fractions — a pure algebraic identity, the value
                                    of each term is unchanged:
                                </p>
                                <F>{"$1/(n(n+1)) = 1/n - 1/(n+1)$"}</F>
                                <p>
                                    The rewritten sum telescopes — successive terms cancel, leaving only
                                    the boundary terms:
                                </p>
                                <F>{"$sum_(n=1)^N (1/n - 1/(n+1)) = 1 - 1/(N+1)$"}</F>
                            </div>
                        </div>
                    </div>

                    {/* Operation */}
                    <div className="rog-method-card">
                        <div className="rog-method-header operation">
                            <div className="rog-letter">O</div>
                            <div>
                                <h2>Operation — Force a Transformation</h2>
                                <p>Apply an operation that changes the form but reveals hidden structure.</p>
                            </div>
                        </div>
                        <div className="rog-method-body">
                            <div className="rog-definition">
                                <h4>Formal Definition</h4>
                                <p>
                                    Define the operator <MathJax inline dynamic>{"$\"Op\"$"}</MathJax> such that:
                                </p>
                                <F>{"$\"Op\"(T) = f(T_(k1), T_(k2), ..., T_(kn))$"}</F>
                                <div className="rog-condition">
                                    <span className="rog-tag">{"Op(T) ≠ T  (expression changes)"}</span>
                                    <span className="rog-tag">{"reveals a recognizable structure"}</span>
                                </div>
                            </div>
                            <div className="rog-example">
                                <h4>Example — Series Transformation</h4>
                                <p>Let:</p>
                                <F>{"$S(x) = sum_(n=0)^oo ((-1)^n)/(n+1) e^(-nx)$"}</F>
                                <p>
                                    Multiply by <MathJax inline dynamic>{"$e^(-x)$"}</MathJax> and
                                    reindex with <MathJax inline dynamic>{"$m = n+1$"}</MathJax>:
                                </p>
                                <F>{"$e^(-x) S(x) = sum_(m=1)^oo ((-1)^(m-1))/m e^(-mx)$"}</F>
                                <p>
                                    With <MathJax inline dynamic>{"$y = e^(-x)$"}</MathJax>, the right side
                                    is the Taylor series of <MathJax inline dynamic>{"$ln(1+y)$"}</MathJax>:
                                </p>
                                <F>{"$e^(-x) S(x) = ln(1 + e^(-x)) quad => quad S(x) = e^x ln(1+e^(-x))$"}</F>
                                <p>
                                    The operation (multiply by <MathJax inline dynamic>{"$e^(-x)$"}</MathJax>) shifted
                                    the series into a recognizable closed form.
                                </p>
                            </div>

                            <div className="rog-example">
                                <h4>Example — Integration by Parts</h4>
                                <p>Compute:</p>
                                <F>{"$int x e^x dx$"}</F>
                                <p>
                                    Apply the IBP operator with <MathJax inline dynamic>{"$u = x, dv = e^x dx$"}</MathJax>.
                                    The operator maps the unknown integral to a known one in <MathJax inline dynamic>{"$cc K$"}</MathJax>:
                                </p>
                                <F>{"$int u dv = uv - int v du$"}</F>
                                <F>{"$int x e^x dx = x e^x - int e^x dx = e^x (x - 1) + C$"}</F>
                                <p>
                                    The IBP rule itself encodes the inverse — the original integral is
                                    fully recovered from the simpler one.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Generation */}
                    <div className="rog-method-card">
                        <div className="rog-method-header generation">
                            <div className="rog-letter">G</div>
                            <div>
                                <h2>Generation — Indirect Construction</h2>
                                <p>Construct the target object as the result of operations on known terms.</p>
                            </div>
                        </div>
                        <div className="rog-method-body">
                            <div className="rog-definition">
                                <h4>Formal Definition</h4>
                                <p>
                                    We define the relationship inversely — <MathJax inline dynamic>{"$T$"}</MathJax> is
                                    not directly manipulated but <em>generated</em>:
                                </p>
                                <F>{"$\"Op\"(T_(k1), T_(k2), ..., T_(kn)) = f(T)$"}</F>
                                <div className="rog-condition">
                                    <span className="rog-tag">{"T is not directly touched"}</span>
                                    <span className="rog-tag">{"T emerges from operations on known objects"}</span>
                                </div>
                            </div>
                            <div className="rog-example">
                                <h4>Example — Gaussian Integral</h4>
                                <p>We seek:</p>
                                <F>{"$I = int_(-oo)^(+oo) e^(-x^2) dx$"}</F>
                                <p>
                                    Instead of computing <MathJax inline dynamic>{"$I$"}</MathJax> directly,
                                    generate <MathJax inline dynamic>{"$I^2$"}</MathJax> from two known integrals:
                                </p>
                                <F>{"$I^2 = (int_(-oo)^(+oo) e^(-x^2) dx)(int_(-oo)^(+oo) e^(-y^2) dy) = int_(RR^2) e^(-(x^2+y^2)) dx dy$"}</F>
                                <p>Switch to polar coordinates <MathJax inline dynamic>{"$(x^2+y^2 = r^2, dx dy = r dr d theta)$"}</MathJax>:</p>
                                <F>{"$I^2 = int_0^(2pi) int_0^oo e^(-r^2) r dr d theta = 2pi * 1/2 = pi$"}</F>
                                <p>Therefore:</p>
                                <F>{"$I = sqrt(pi)$"}</F>
                            </div>

                            <div className="rog-example">
                                <h4>Example — Sum from Geometric Series</h4>
                                <p>Compute:</p>
                                <F>{"$T = sum_(n=1)^oo n x^n, quad |x| < 1$"}</F>
                                <p>
                                    The target <MathJax inline dynamic>{"$T$"}</MathJax> is never directly
                                    manipulated. Start from a known term in <MathJax inline dynamic>{"$cc K$"}</MathJax>:
                                </p>
                                <F>{"$sum_(n=0)^oo x^n = 1/(1-x)$"}</F>
                                <p>Differentiate both sides — an operation on the known term:</p>
                                <F>{"$sum_(n=1)^oo n x^(n-1) = 1/(1-x)^2$"}</F>
                                <p>Multiply by <MathJax inline dynamic>{"$x$"}</MathJax> to generate the target:</p>
                                <F>{"$sum_(n=1)^oo n x^n = x/(1-x)^2$"}</F>
                                <p style={{fontSize: "0.85rem", color: "#666"}}>
                                    The target sum emerged as the result of operations on a known series —
                                    classic Generation.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Summary */}
                <div className="rog-summary">
                    <h3>Summary</h3>
                    <div className="rog-summary-row">
                        <div className="rog-summary-badge badge-r">R</div>
                        <div className="rog-summary-formula">
                            <MathJax dynamic>
                                {"$\"Rew\"(T) = f(T_(k1), ..., T_(kn)), quad \"Rew\"(T) = T$"}
                            </MathJax>
                        </div>
                    </div>
                    <div className="rog-summary-row">
                        <div className="rog-summary-badge badge-o">O</div>
                        <div className="rog-summary-formula">
                            <MathJax dynamic>
                                {"$\"Op\"(T) = f(T_(k1), ..., T_(kn)), quad \"Op\"(T) != T$"}
                            </MathJax>
                        </div>
                    </div>
                    <div className="rog-summary-row">
                        <div className="rog-summary-badge badge-g">G</div>
                        <div className="rog-summary-formula">
                            <MathJax dynamic>
                                {"$\"Op\"(T_(k1), ..., T_(kn)) = f(T)$"}
                            </MathJax>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ROGFrameworkPage;
